import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { asyncToast } from '@/components/ui/Toast/asyncToast'
import { Queries } from '@/api/queries.enum'
import { Mutations } from '@/api/mutations.enum'
import { getAbsolute, getNotificationCount, getNotifications } from '@/api/actions/notifications/notificationActions'
import {
    CLEANUP_INTERVAL,
    DISMISS_FILTER_DURATION,
    getNotificationErrorMessage,
    POLLING_PAUSE_DURATION,
    UI_PAGE_SIZE,
    MIN_VISIBLE_NOTIFICATIONS_THRESHOLD,
    POLLING_INTERVAL_ACTIVE,
    POLLING_INTERVAL_HIDDEN,
    QUERY_STALE_TIME,
    QUERY_GC_TIME,
    HTTP_CLIENT_ERROR_START,
    HTTP_SERVER_ERROR_START,
    MAX_RETRY_ATTEMPTS,
} from '@/api/actions/notifications/notificationConstants'
import type { AppError } from '@/utils/errorHandling'
import { useVisibilityAwareInterval } from '@/hooks/useVisibilityAwareInterval'
import { useDismissNotification } from '@/hooks/notifications/useDismissNotification'
import { useDismissAllNotifications } from '@/hooks/notifications/useDismissAllNotifications'
import { useSyncNotificationHandler } from '@/hooks/integrations/useSyncNotificationHandler'
import type { UserType } from '@/features/notifications/types/notifications.types'
import type { UseNotificationsReturn } from '@/features/notifications/hooks/useNotifications'
import type { NotificationListResponse, NotificationItem } from '@/api/actions/notifications/notificationActions.types'
import { store } from '@/store/store'

export function useRealTimeNotifications(
    initialUserType: UserType = 'manager',
    playNotification: () => void,
): UseNotificationsReturn {
    const [lastCount, setLastCount] = useState<number | undefined>(undefined) // Start as undefined
    const [userType, setUserType] = useState<UserType>(initialUserType)
    const [nextPageUrl, setNextPageUrl] = useState<string | null>(null)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [pollingPaused, setPollingPaused] = useState(false)
    const [recentDismiss, setRecentDismiss] = useState<Map<string, number>>(new Map())

    const pendingIds = useRef(new Set<string>())
    const currentDismissId = useRef<string | null>(null)
    const queryClient = useQueryClient()

    const markDismissed = (id: string) => {
        setRecentDismiss((m) => new Map(m).set(id, Date.now() + DISMISS_FILTER_DURATION))
    }

    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            setRecentDismiss((m) => {
                const now = Date.now()
                const next = new Map(m)
                for (const [k, v] of Array.from(next.entries())) {
                    if (v < now) next.delete(k)
                }
                return next
            })
        }, CLEANUP_INTERVAL)

        return () => clearInterval(cleanupInterval)
    }, [])

    const dismissMutation = useDismissNotification({
        onDismissStart: (notificationId) => {
            currentDismissId.current = notificationId
            pendingIds.current.add(notificationId)
            markDismissed(notificationId)
        },
        onSuccess: (data, notificationId) => {
            pendingIds.current.delete(notificationId)
            currentDismissId.current = null
        },
        onError: (_error: AxiosError) => {
            if (currentDismissId.current) {
                pendingIds.current.delete(currentDismissId.current)
                currentDismissId.current = null
            }
        },
    })
    const dismissAllMutation = useDismissAllNotifications({
        onSuccess: () => {
            setRecentDismiss(new Map())
            pendingIds.current.clear()
        },
    })

    const getErrorMsg = (error: unknown, context: 'FETCH_FAILED' | 'COUNT_FAILED'): string | null => {
        if (!error) return null
        return getNotificationErrorMessage(error as AppError, context)
    }

    const {
        data: currentCount = 0,
        error: countError,
        refetch: refetchCount,
    } = useQuery({
        queryKey: [Queries.getNotificationCount],
        queryFn: () => getNotificationCount(),
        staleTime: QUERY_STALE_TIME,
        gcTime: QUERY_GC_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: (failureCount, error: AxiosError) => {
            if (
                error?.response?.status &&
                error.response.status >= HTTP_CLIENT_ERROR_START &&
                error.response.status < HTTP_SERVER_ERROR_START
            ) {
                return false
            }
            return failureCount < MAX_RETRY_ATTEMPTS
        },
    })

    const {
        data: notificationData,
        isLoading: isNotificationsLoading,
        error: notificationsError,
        refetch: refetchNotifications,
    } = useQuery({
        queryKey: [Queries.getNotifications, 1, UI_PAGE_SIZE],
        queryFn: () => getNotifications({ page: 1, page_size: UI_PAGE_SIZE }),
        staleTime: QUERY_STALE_TIME,
        gcTime: QUERY_GC_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        enabled: false,
    })

    const isMutatingNotifications =
        useIsMutating({
            mutationKey: [Mutations.dismissNotification],
        }) +
            useIsMutating({
                mutationKey: [Mutations.dismissAllNotifications],
            }) >
        0

    useEffect(() => {
        if (isMutatingNotifications) {
            setPollingPaused(true)
        } else if (pollingPaused) {
            const timer = setTimeout(() => {
                setPollingPaused(false)
            }, POLLING_PAUSE_DURATION)

            return () => clearTimeout(timer)
        }
    }, [isMutatingNotifications, pollingPaused])

    const shouldPoll = !isMutatingNotifications && !pollingPaused

    useVisibilityAwareInterval(
        () => {
            refetchCount()
        },
        {
            activeMs: POLLING_INTERVAL_ACTIVE,
            hiddenMs: POLLING_INTERVAL_HIDDEN,
            enabled: shouldPoll,
        },
    )

    useEffect(() => {
        if (lastCount === undefined) {
            setLastCount(currentCount)
            return
        }

        if (isMutatingNotifications) {
            setLastCount(currentCount)
            return
        }

        // Play sound when count increases (new notification)
        if (currentCount > (lastCount || 0)) {
            playNotification()
            // Refetch notifications when count increases to get fresh data
            void refetchNotifications()
        }

        setLastCount(currentCount)
    }, [currentCount, lastCount, refetchNotifications, isMutatingNotifications, playNotification])

    useEffect(() => {
        if (notificationData) {
            const response = notificationData as NotificationListResponse
            setNextPageUrl(response.next)
        }
    }, [notificationData])

    const rawNotifications = useMemo(
        () => (notificationData as NotificationListResponse)?.results || [],
        [notificationData],
    )
    const allNotifications = rawNotifications.filter(
        (notification: NotificationItem) => (recentDismiss.get(notification.id) ?? 0) < Date.now(),
    )

    useSyncNotificationHandler(
        allNotifications.map((item: NotificationItem) => ({
            ...item,
            notification_type: 'info' as const,
        })),
    )

    const loadMoreNotificationsFunc = useCallback(async (): Promise<void> => {
        if (!nextPageUrl || isLoadingMore) return

        setIsLoadingMore(true)
        try {
            const response: NotificationListResponse = nextPageUrl
                ? await getAbsolute<NotificationListResponse>(nextPageUrl)
                : await getNotifications({ page: 2, page_size: UI_PAGE_SIZE })
            queryClient.setQueryData(
                [Queries.getNotifications, 1, UI_PAGE_SIZE],
                (old: NotificationListResponse | undefined) => {
                    if (!old) return response

                    const seen = new Set(old.results.map((n: NotificationItem) => n.id))
                    const merged = [
                        ...old.results,
                        ...(response.results ?? []).filter((n: NotificationItem) => !seen.has(n.id)),
                    ]

                    return {
                        ...response,
                        results: merged,
                    }
                },
            )

            setNextPageUrl(response.next)
        } catch {
        } finally {
            setIsLoadingMore(false)
        }
    }, [nextPageUrl, isLoadingMore, queryClient])

    // Optional: when a welcome notification arrives, set banner for congrats handoff
    useEffect(() => {
        const orgUuid = store.getState().auth.organization?.uuid
        if (!orgUuid) return
        const special = rawNotifications.find(
            (n: NotificationItem) => n.template_key === 'optimo_core.onboarding.welcome_complete',
        )
        if (special) {
            try {
                sessionStorage.setItem(`onboard:banner:${orgUuid}`, 'org')
            } catch {}
        }
    }, [rawNotifications])

    const actions = {
        setUserType: (newUserType: UserType) => {
            setUserType(newUserType)
        },
        addNotification: () => {
            refetchCount()
        },
        dismissNotification: (notificationId: string) => {
            if (pendingIds.current.has(notificationId)) {
                return
            }
            dismissMutation.mutate(notificationId)
        },
        clearAllNotifications: () => {
            // Get the current count of notifications that will be dismissed
            const notificationsCount = allNotifications.length

            asyncToast({
                id: 'dismiss-all-notifications',
                promise: dismissAllMutation.mutateAsync(),
                msgs: {
                    loading: 'Dismissing all notifications...',
                    success: (data) => {
                        const dismissedCount = data?.dismissed_count || notificationsCount
                        return `All ${dismissedCount} notifications dismissed`
                    },
                    error: 'Failed to dismiss notifications',
                },
            })
        },
        simulateNewNotification: () => {
            refetchCount()
        },
        refreshNotifications: async (): Promise<void> => {
            await Promise.all([refetchCount(), refetchNotifications()])
        },
        refreshNotificationsList: async (): Promise<void> => {
            const result = await refetchNotifications()
            if (result.data?.count !== undefined) {
                queryClient.setQueryData([Queries.getNotificationCount], result.data.count)
            }
        },
        loadMoreNotifications: loadMoreNotificationsFunc,
        isDismissing: (notificationId: string): boolean => {
            return pendingIds.current.has(notificationId)
        },
    }

    const totalNotifications = (notificationData as NotificationListResponse)?.count || currentCount
    const prevLastCountRef = useRef<number | undefined>(undefined)

    useEffect(() => {
        const prevLastCount = prevLastCountRef.current
        const countJustIncreased = currentCount > (prevLastCount || 0)

        const shouldRefetch =
            allNotifications.length <= MIN_VISIBLE_NOTIFICATIONS_THRESHOLD &&
            totalNotifications > allNotifications.length &&
            !isLoadingMore &&
            !isMutatingNotifications &&
            !countJustIncreased

        prevLastCountRef.current = lastCount

        if (shouldRefetch) {
            void refetchNotifications()
        }
    }, [
        allNotifications.length,
        totalNotifications,
        lastCount,
        currentCount,
        isLoadingMore,
        isMutatingNotifications,
        refetchNotifications,
    ])

    const state = {
        notifications: allNotifications,
        userType,
        isLoading: isNotificationsLoading || isMutatingNotifications || isLoadingMore,
        isDismissingAll: dismissAllMutation.isPending,
        error: getErrorMsg(countError, 'COUNT_FAILED') || getErrorMsg(notificationsError, 'FETCH_FAILED'),
        lastNotificationId: null,
        hasNewNotification: currentCount > (lastCount || 0),
        totalNotifications,
        loadedNotifications: allNotifications.length,
        hasMoreNotifications: !!nextPageUrl,
        isLoadingMore,
    }

    return {
        state,
        actions,
        unreadCount: currentCount,
    }
}
